import React, { useEffect, useRef } from 'react';

const SITE_KEY = (process.env.REACT_APP_TURNSTILE_SITE_KEY || '').trim();
const SCRIPT_ID = 'cf-turnstile-script';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

/**
 * Cloudflare Turnstile widget. Calls onTokenChange with the token (or '').
 * Handles React Strict Mode remounts safely.
 */
const TurnstileWidget = ({ onTokenChange }) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onTokenChangeRef = useRef(onTokenChange);

  useEffect(() => {
    onTokenChangeRef.current = onTokenChange;
  }, [onTokenChange]);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.__SCORECLASH_TURNSTILE_E2E__) {
      onTokenChangeRef.current?.('e2e-turnstile-token');
      return undefined;
    }

    if (!SITE_KEY) {
      onTokenChangeRef.current?.('');
      return undefined;
    }

    let cancelled = false;
    let loadListener = null;
    let scriptEl = null;

    const emit = (token) => {
      if (!cancelled) onTokenChangeRef.current?.(token || '');
    };

    const clearWidget = () => {
      if (widgetIdRef.current != null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* ignore */
        }
        widgetIdRef.current = null;
      }
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };

    const renderWidget = () => {
      if (cancelled || !containerRef.current || !window.turnstile) return;
      clearWidget();
      try {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          theme: 'dark',
          size: 'flexible',
          callback: (token) => emit(token),
          'expired-callback': () => emit(''),
          'error-callback': (code) => {
            console.warn('Turnstile error-callback', code);
            emit('');
          }
        });
      } catch (err) {
        console.warn('Turnstile render failed', err);
        emit('');
      }
    };

    const start = () => {
      if (cancelled) return;
      if (window.turnstile) {
        requestAnimationFrame(() => {
          if (!cancelled) renderWidget();
        });
        return;
      }

      scriptEl = document.getElementById(SCRIPT_ID);
      if (!scriptEl) {
        scriptEl = document.createElement('script');
        scriptEl.id = SCRIPT_ID;
        scriptEl.src = SCRIPT_SRC;
        scriptEl.async = true;
        document.head.appendChild(scriptEl);
      }

      if (window.turnstile) {
        requestAnimationFrame(() => {
          if (!cancelled) renderWidget();
        });
        return;
      }

      loadListener = () => {
        if (!cancelled) renderWidget();
      };
      scriptEl.addEventListener('load', loadListener);
    };

    start();

    return () => {
      cancelled = true;
      if (scriptEl && loadListener) {
        scriptEl.removeEventListener('load', loadListener);
      }
      clearWidget();
    };
  }, []);

  if (!SITE_KEY) {
    return (
      <div className="turnstile-widget turnstile-widget--missing" data-testid="turnstile-widget">
        Captcha is not configured.
      </div>
    );
  }

  return <div className="turnstile-widget" ref={containerRef} data-testid="turnstile-widget" />;
};

export default TurnstileWidget;
